/**
 * /well-servicing/rates/new and /well-servicing/rates/:id — one rate on
 * the Well Servicing rate card. Full pages, no dialogs; the delete is confirmed inline.
 *
 * Deleting a rate leaves every ticket line already priced from it alone (the
 * line keeps its copied rate, code and name) — the confirm says so.
 */
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useWsCustomers, useWsEquipmentOptions, useWsMeta } from '../../../hooks/useWellServicing';
import { useCreateWsRate, useDeleteWsRate, useWsEquipmentTypes, useWsRate, useWsRates, useUpdateWsRate } from '../../../hooks/useWsTickets';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { canWriteWsTickets, wsTicketRefusal } from '../../../lib/ws-tickets';
import { catalogLabel, wsDay } from '../../../lib/well-servicing';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, WS_EYEBROW, WsEmpty, WsLoading, WsPanel, WsSpec } from '../WsBits';
import { PfInlineConfirm as WsInlineConfirm } from '../../pipeline-facility/digs/DigBits';
import { WsRateForm, type WsRateFormOptions } from './RateForm';
import { wsRateAppliesTo } from './RatesScreen';
import { wsRatePriceText } from './TicketBits';
import type { WsTicketCatalog } from '../../../types/ws-tickets';

function useRateFormOptions(enabled: boolean): WsRateFormOptions {
  const customers = useWsCustomers(enabled).data ?? [];
  const equipment = useWsEquipmentOptions(enabled).data ?? [];
  const equipmentTypes = useWsEquipmentTypes(enabled).data ?? [];
  return { customers, equipment, equipmentTypes };
}

export function WsNewRateScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const back = useBackToList('/well-servicing/rates');
  const catalog = (useWsMeta().data?.data ?? null) as WsTicketCatalog | null;
  const metaQuery = useWsRates();
  const options = useRateFormOptions(true);
  const create = useCreateWsRate();
  const [error, setError] = useState<string | null>(null);
  const meta = metaQuery.data?.meta ?? null;
  return (
    <div data-testid="ws-rate-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WS_EYEBROW} title="New rate" description="One line on the rate card." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">
          <WsPanel eyebrow="Rate card" title="New rate">
            <WsRateForm
              catalog={catalog}
              options={options}
              currency={meta?.currency ?? null}
              today={meta?.today ?? null}
              defaultCustomerId={searchParams.get('customerId') ?? ''}
              pending={create.isPending}
              error={error}
              submitLabel="Add rate"
              onCancel={back}
              onSubmit={(body) => {
                setError(null);
                create.mutate(body, {
                  onSuccess: (r) => {
                    toast.success('Rate added');
                    navigate(r.data ? `/well-servicing/rates/${r.data.id}` : '/well-servicing/rates', { replace: true });
                  },
                  onError: (e) => setError(wsTicketRefusal(e, 'The rate could not be added.')),
                });
              }}
            />
          </WsPanel>
        </div>
      </section>
    </div>
  );
}

export default function WsRateDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/well-servicing/rates');
  const { user } = useAuth();
  const canWrite = canWriteWsTickets(user?.role);
  const query = useWsRate(id);
  const catalog = (useWsMeta().data?.data ?? null) as WsTicketCatalog | null;
  const metaQuery = useWsRates(undefined, canWrite);
  const update = useUpdateWsRate();
  const del = useDeleteWsRate();
  const [editing, setEditing] = useState(false);
  const options = useRateFormOptions(canWrite && editing);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const r = query.data?.data ?? null;
  const failure = queryLoadFailure(query);

  return (
    <div data-testid="ws-rate-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title={r?.code || (query.isLoading ? 'Rate' : '—')}
        description={r ? `${r.name} · ${r.customerId ? r.customerName ?? '—' : 'Default card'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          r && canWrite ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); }} disabled={editing}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WsLoading label="Loading rate" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !r) ? (
            <WsPanel eyebrow="Rate card" title="Rate">
              <WsEmpty>This rate was not found. It may have been deleted.</WsEmpty>
            </WsPanel>
          ) : failure || !r ? (
            <WsPanel eyebrow="Rate card" title="Rate">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this rate" />
            </WsPanel>
          ) : (
            <>
              {deleting && (
                <WsInlineConfirm
                  testId="ws-rate-delete-confirm"
                  message={`Delete ${r.code} from the rate card? Ticket lines already priced from it keep their price. A rate a rent-in bills at cannot be deleted.`}
                  confirmLabel="Delete rate"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(r.id, {
                      onSuccess: () => {
                        toast.success('Rate deleted');
                        navigate('/well-servicing/rates', { replace: true });
                      },
                      onError: (e) => setDeleteError(wsTicketRefusal(e, 'The rate could not be deleted.')),
                    })
                  }
                />
              )}
              {editing && canWrite ? (
                <WsPanel eyebrow="Edit" title={`Edit ${r.code}`}>
                  <WsRateForm
                    rate={r}
                    catalog={catalog}
                    options={options}
                    currency={r.currency}
                    today={metaQuery.data?.meta.today ?? null}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save rate"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      update.mutate(
                        { id: r.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Rate saved');
                            setEditing(false);
                          },
                          onError: (e) => setEditError(wsTicketRefusal(e, 'The rate could not be saved.')),
                        },
                      );
                    }}
                  />
                </WsPanel>
              ) : (
                <WsPanel eyebrow="Rate card" title="Details" testId="ws-rate-details">
                  <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                    <WsSpec label="Code" value={r.code || '—'} mono />
                    <WsSpec label="Name" value={r.name || '—'} />
                    <WsSpec label="Card" value={r.customerId ? r.customerName || '—' : 'Default card'} />
                    <WsSpec label="Kind" value={r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind)} />
                    <WsSpec label={r.kind === 'third_party' ? 'Pricing' : 'Rate'} value={wsRatePriceText(r)} mono />
                    <WsSpec label="Unit" value={r.unitLabel || catalogLabel(catalog?.priceUnits, r.unit)} />
                    {r.volumeUnit && <WsSpec label="Volume unit" value={r.volumeUnit} />}
                    <WsSpec label="Currency" value={r.currency || '—'} />
                    <WsSpec label="Applies to" value={wsRateAppliesTo(r, catalog?.crewPositions)} />
                    <WsSpec label="Starts" value={wsDay(r.effectiveFrom)} />
                    <WsSpec label="Ends" value={r.effectiveTo ? wsDay(r.effectiveTo) : 'No end'} />
                  </dl>
                  {(r.description || r.notes) && (
                    <div className="space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                      {r.description && <p>{r.description}</p>}
                      {r.notes && <p className="text-fieldiq-muted">{r.notes}</p>}
                    </div>
                  )}
                </WsPanel>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
